import type { FunctionComponent } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import {
  Button,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { DynamicContents, DynamicContentsItem } from './types';
import { DynamicContentName } from './types';
import { WebLink } from '../WebLink/WebLink';

interface DynamicContentProps {
  dynamicContents: DynamicContents<DynamicContentsItem>;
  textColorVariant?: keyof DefaultTheme['palette'];
  className?: string;
}

const blurredTextStyling = css`
  filter: blur(calc(${spacingHelper('twoExtraSmall')} / 4 * 4.5));
  user-select: none;
`;

const DynamicContentText = styled(Text)<{
  $colorVariant?: keyof DefaultTheme['palette'];
  $bold?: boolean;
  $blur?: boolean;
}>`
  color: ${(props) => paletteHelper(props.$colorVariant || 'textPrimary')};
  ${({ $bold, $colorVariant }) =>
    $bold &&
    css`
      ${typographyHelper('body01Bold')}
      color: ${paletteHelper($colorVariant || 'textPrimary')};
    `}
  ${({ $blur }) => $blur && blurredTextStyling}
`;

const spacingIconRight = css`
  margin-right: ${spacingHelper('twoExtraSmall')};
  white-space: nowrap;
`;

const spacingIconLeft = css`
  margin-left: ${spacingHelper('twoExtraSmall')};
  white-space: nowrap;
`;

const DynamicContentWebLink = styled(WebLink)`
  /* If icon placement is not provided, ConstructKit defaults to placement left */
  ${(props) =>
    props.iconInfo && (props.iconInfo.placement === 'right' ? spacingIconRight : spacingIconLeft)}
`;

export const DynamicContent: FunctionComponent<DynamicContentProps> = ({
  dynamicContents,
  textColorVariant,
  className,
}) => {
  const contents = dynamicContents
    .map((content, index) => {
      if (content.name === DynamicContentName.Text)
        return (
          <DynamicContentText
            key={`${content.text}-${index}`}
            forwardedAs={content.mark ? 'mark' : 'span'}
            $colorVariant={textColorVariant}
            $bold={content.bold ?? undefined}
            $blur={content.blur ?? undefined}
            className={className}
          >
            {content.text}
          </DynamicContentText>
        );
      if (content.name === DynamicContentName.WebLink) {
        return (
          <DynamicContentWebLink
            key={`${content.url}-${index}`}
            title={content.title}
            url={content.url}
            isExternal={content.isExternal}
            variant={content.variant}
            iconInfo={content.iconInfo}
            tracking={content.tracking}
            className={className}
            dangerouslySetClassNames={{ iconWrapper: 'iconWrapper' }}
            rel={content.rel}
            disabled={content.disabled}
          />
        );
      }
      if (content.name === DynamicContentName.Button) {
        return (
          <Button
            key={`${content.text}-${index}`}
            variant={content.buttonVariant}
            className={className}
            aria-label={content.ariaLabel ?? undefined}
            onClick={content.onClick}
          >
            {content.text}
          </Button>
        );
      }
      return null;
    })
    .filter(Boolean);
  return <>{contents}</>;
};
